import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NavItem } from '@components/layout/layout.type';
import { SELECT_VENUE_ROUTE } from '@constants/routes';
import { FeatureFlagContext } from '@contexts/feature-flag/feature-flag-context';
import { useAppLaunchDetection } from '@hooks/use-app-launch-detection';
import { useMainSidebarMenu } from '@hooks/use-main-sidebar-menu';
import { getLastPathCookie } from '@utils/last-path-storage/get-last-path-cookie';
import { setLastPathCookie } from '@utils/last-path-storage/set-last-path-cookie';

import { NavigationContext } from './navigation-context';

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [navigationItems, setNavigationItems] = useState<NavItem[]>([]);
  const [isItemClicked, setIsItemClicked] = useState<boolean>(false);
  const [preventRedirect, setPreventRedirect] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const location = useLocation();
  const sidebarMenu = useMainSidebarMenu();
  const { isWebView } = useContext(FeatureFlagContext);
  const { isInitialLoad, isInitialRedirect } = useAppLaunchDetection();

  // Store the full path including query parameters, but be smart about when to save
  const fullPath = `${location.pathname}${location.search}`;

  const [route, setRoute] = useState(() => ({
    to: fullPath,
    from: fullPath,
  }));

  // Save venue information when navigating
  useEffect(() => {
    // Read the store (and save) only for a webview navigation that can persist — skip the ~100% of
    // non-webview navigations and this mount's first 200 ms. That window is the only thing holding a save back
    // while a restore reloads: no flag tells this provider a restore is in flight.
    const isWebViewSave = isWebView && !isInitialLoad && !isInitialRedirect && fullPath !== SELECT_VENUE_ROUTE;
    if (!isWebViewSave) return;

    // Suppress a query appended to the page we are already on. This also drops a deliberate same-page
    // query change (e.g. a tab switch) from the store — acceptable collateral: relaunching to the bare
    // page beats churning the store on every filter/search keystroke and resuming on a stale transient
    // query. Compare the stored path's pathname, not a substring of the whole stored value — the
    // substring test false-matched unrelated paths and, on an empty store, dropped the very first save.
    const storedPathname = getLastPathCookie()?.split('?')[0];
    if (location.search !== '' && storedPathname === location.pathname) return;

    setLastPathCookie(fullPath);
  }, [isWebView, fullPath, isInitialLoad, isInitialRedirect, location.pathname, location.search]);

  useEffect(() => {
    if (fullPath !== route.to) {
      setRoute((prev) => ({ to: fullPath, from: prev.to }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullPath]);

  useEffect(() => {
    setNavigationItems(sidebarMenu ?? []);
  }, [sidebarMenu]);

  const contextValue = useMemo(
    () => ({
      navigationItems,
      isItemClicked,
      preventRedirect,
      redirectUrl,
      route,
      setNavigation: setNavigationItems,
      setIsItemClicked,
      setPreventRedirect,
      setRedirectUrl,
    }),
    [navigationItems, isItemClicked, preventRedirect, redirectUrl, route]
  );

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};
